<template>
  <div
    v-if="isSmall"
    class="tw:flex-wrap tw:lg:flex-nowrap tw:gap-y-1 tw:border tw:border-t-0 tw:lg:border-t tw:lg:border-l-0 tw:border-gray-300 tw:flex tw:flex-row tw:items-center tw:h-auto tw:gap-x-2 tw:p-3 tw:rounded-b-lg tw:lg:rounded-bl-none tw:lg:rounded-r-lg"
  >
    <p
      class="title tw:font-bold tw:inline tw:text-sm tw:items-start tw:gap-x-1"
      v-html="$t('rc_commission_header_settings')"
    />

    <p class="tw:text-sm tw:border tw:border-gray-300 tw:rounded tw:px-2 tw:py-1">
      <span class="tw:font-bold tw:text-sm">{{ commissionRate }}</span>
    </p>
  </div>
  <Card
    v-else
    :isCustomTooltip="false"
    :title="$t('rc_commission_header_settings')"
    :hint="$t('rc_ncpa_commission_tooltip')"
    :isActionable="isActionable"
    type="commissionRate"
    :hasHint="hasHint"
    :preventHoverEffect="isHoveringSmartBoost"
  >
    <template #cardData>
      <span v-if="!shouldShowSmartBoost">{{ commissionRate }}</span>

      <!-- Never had any boost -->
      <p
        v-if="neverHadBoosts && shouldShowSmartBoost"
        class="tw:text-sm tw:border tw:border-gray-300 tw:rounded tw:px-2 tw:py-1"
      >
        <span class="tw:font-bold tw:text-sm">{{ commissionRate }}</span>
      </p>

      <!-- Has active boosts -->
      <UiTooltip theme="customdarkwithpadding" tooltip-position="bottom" :label="$t('rc_smartboost_tooltip_active')">
        <div
          v-if="hasActiveBoosts && shouldShowSmartBoost"
          class="tw:inline-flex tw:items-center tw:text-sm tw:rounded tw:overflow-hidden"
          @mouseenter="isHoveringSmartBoost = true"
          @mouseleave="isHoveringSmartBoost = false"
        >
          <span class="tw:rounded-l tw:border tw:border-r-0 tw:border-gray-200 tw:px-2 tw:py-1">
            <strong>{{ commissionRate }}</strong>
          </span>
          <router-link
            to="/rate-connect/running/smart-boost"
            :aria-label="$t('rc_smartboost_status_active')"
            :class="activeColors"
            class="tw:border tw:px-2 tw:py-1 tw:whitespace-nowrap"
            @click.stop
          >
            <span class="tw:lg:hidden tw:xl:block" v-html="$t('rc_smartboost_status_active')" />
            <span class="tw:hidden tw:lg:flex tw:xl:hidden">{{ $t('rc_smartboost_tab') }}</span>
          </router-link>
        </div>
      </UiTooltip>

      <!-- Had boosts in the past -->
      <UiTooltip theme="customdarkwithpadding" tooltip-position="bottom" :label="$t('rc_smartboost_tooltip_expired')">
        <div
          v-if="hadBoostsInThePast && !hasActiveBoosts && shouldShowSmartBoost"
          class="tw:inline-flex tw:items-center tw:text-sm tw:rounded tw:overflow-hidden"
          @mouseenter="isHoveringSmartBoost = true"
          @mouseleave="isHoveringSmartBoost = false"
        >
          <span class="tw:rounded-l tw:border tw:border-r-0 tw:border-gray-300 tw:px-2 tw:py-1">
            <strong>{{ commissionRate }}</strong>
          </span>
          <router-link
            to="/rate-connect/running/smart-boost-history"
            class="tw:bg-gray-200 tw:border tw:border-gray-200 tw:px-2 tw:py-1 tw:whitespace-nowrap tw:text-gray-600"
            @click.stop
            :aria-label="$t('rc_smartboost_status_expired')"
          >
            <span class="tw:lg:hidden tw:xl:block" v-html="$t('rc_smartboost_status_expired')" />
            <span class="tw:hidden tw:lg:flex tw:xl:hidden">{{ $t('rc_smartboost_tab') }}</span>
          </router-link>
        </div>
      </UiTooltip>
    </template>
    <template #dropdownMenu="{ closeMenu }">
      <div role="menu" aria-label="Commission rate options">
        <button
          v-for="commission in allCommissions"
          :key="commission"
          class="tw:w-full tw:px-4 tw:py-3 tw:text-sm tw:flex tw:flex-col tw:items-start tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:first:rounded-t-lg tw:last:rounded-b-lg"
          :class="
            cpa === commission
              ? 'tw:bg-blue-200 tw:text-blue-800 tw:cursor-default'
              : 'tw:cursor-pointer tw:bg-white tw:text-black'
          "
          @click="openChangeCommissionLevelModal(commission, closeMenu)"
          :aria-current="cpa === commission ? 'true' : undefined"
          :disabled="cpa === commission"
          type="button"
          role="menuitem"
        >
          <span>{{ formatPercentage(language, commission, 0, 0) }}</span>
        </button>
      </div>
    </template>
  </Card>
</template>
<script setup>
import { computed, ref } from 'vue'

import Card from '@/components/rateConnect/statusBar/Card.vue'
import { useActiveBoosts, useBoostsHistory, useCommissionLevels } from '@/components/rateConnect/queries'
import { formatPercentage } from '@/utils/Utility.ts'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'

import appAnalytics from '@/utils/tracking'
import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'

const shouldShowSmartBoost = import.meta.env.VITE_FEATURE_VISIBLE === 'true'

const { language } = useSessionValues()
const props = defineProps({
  isActionable: Boolean,
  hasHint: Boolean,
  campaignStatus: String,
  partnerStatus: String,
  unavailabilityRate: [Number, String],
  cpa: {
    type: Number,
    default: null,
  },
  isSmall: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['openChangeCommissionLevelModal', 'setNewCpa'])
const isHoveringSmartBoost = ref(false)

const { activeBoosts } = useActiveBoosts(selectedAccommodationId)
const { boostsHistory } = useBoostsHistory(selectedAccommodationId)

const neverHadBoosts = computed(
  () => Object.keys(activeBoosts.value || {})?.length === 0 && boostsHistory.value?.length === 0
)

const hadBoostsInThePast = computed(
  () => Object.keys(activeBoosts.value || {})?.length === 0 && boostsHistory.value?.length > 0
)

const hasActiveBoosts = computed(() => Object.keys(activeBoosts.value || {})?.length > 0)

const openChangeCommissionLevelModal = (cpa, closeMenu) => {
  if (props.cpa !== cpa) {
    emit('setNewCpa', cpa)
    emit('openChangeCommissionLevelModal')
    appAnalytics.track(appAnalytics.events.RC_STATUS_BAR_MENU_OPTION_CLICKED, {
      accommodationId: selectedAccommodationId.value,
      cardType: 'commissionRate',
      cpa,
      campaignType: 'netCpa',
    })
    closeMenu()
  }
}
const { eligibleCommissionLevels } = useCommissionLevels()
const allCommissions = computed(() => {
  if (!eligibleCommissionLevels.value) return
  const commissions = [...eligibleCommissionLevels.value]
  if (!commissions.includes(props.cpa)) {
    commissions.unshift(props.cpa)
  }
  return commissions
})
const commissionRate = computed(() => {
  return formatPercentage(language.value, props.cpa, 0, 0)
})

const activeColors = computed(() => {
  if (props.partnerStatus !== 'active' || props.campaignStatus !== 'ACTIVE' || props.unavailabilityRate === 100) {
    return 'tw:bg-gray-200 tw:border-gray-200 tw:text-gray-600'
  } else {
    return 'tw:bg-green-200 tw:border-green-200 tw:text-green-600'
  }
})
</script>
